import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, Loader2, ReceiptText, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, PageState, StatusPill, Empty, Field } from "@/components/app/Common";
import { SimpleSelect, entityOptions, listOptions } from "@/components/app/SimpleSelect";
import { useLoad } from "@/hooks/useLoad";
import { api, blobUrl, errMsg } from "@/lib/api";
import { fmtDate, money } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

const ReviewDialog = ({ receipt, onClose, ents, cats, reload }) => {
  const [f, setF] = useState({});
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!receipt) return;
    setF({ vendor: receipt.vendor || "", date: receipt.date || "", amount: receipt.amount ?? "", tax: receipt.tax ?? "", currency: receipt.currency || "CAD",
      category: receipt.category || "", entity_id: receipt.entity_id || "", notes: receipt.notes || "" });
    setImg(null);
    if (receipt.file_path) blobUrl(`/receipts/${receipt.id}/file`).then(setImg).catch(() => {});
  }, [receipt]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const body = () => ({ ...f, amount: f.amount === "" ? null : Number(f.amount), tax: f.tax === "" ? null : Number(f.tax), category: f.category || null, entity_id: f.entity_id || null });
  const save = async (post) => {
    try {
      await api.put(`/receipts/${receipt.id}`, body());
      if (post) await api.post(`/receipts/${receipt.id}/post`);
      toast.success(post ? "Receipt posted" : "Saved");
      reload();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!receipt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="receipt-review-dialog">
        <DialogHeader><DialogTitle>Review receipt</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-lg border border-border bg-muted/40 min-h-[240px] flex items-center justify-center overflow-hidden">
            {img ? (receipt?.content_type === "application/pdf" ? <iframe title="receipt" src={img} className="w-full h-[380px]" /> : <img src={img} alt="Receipt" className="max-h-[380px] object-contain" data-testid="receipt-preview-img" />)
              : <span className="text-xs text-muted-foreground">{receipt?.file_path ? "Loading preview…" : "No image attached"}</span>}
          </div>
          <div className="space-y-3">
            {receipt?.confidence != null && <div className="text-xs text-muted-foreground">AI extraction confidence: <span className="font-mono">{Math.round(receipt.confidence * 100)}%</span>{receipt.suggested_category && ` · suggested ${receipt.suggested_category}`}</div>}
            <Field label="Vendor"><Input value={f.vendor || ""} onChange={set("vendor")} data-testid="receipt-vendor-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date"><Input type="date" value={f.date || ""} onChange={set("date")} data-testid="receipt-date-input" /></Field>
              <Field label="Currency"><SimpleSelect value={f.currency} onChange={set("currency")} options={listOptions(["CAD", "USD", "EUR", "GBP"])} testId="receipt-currency-select" /></Field>
              <Field label="Amount (total)"><Input type="number" step="0.01" value={f.amount} onChange={set("amount")} data-testid="receipt-amount-input" /></Field>
              <Field label="Tax"><Input type="number" step="0.01" value={f.tax} onChange={set("tax")} data-testid="receipt-tax-input" /></Field>
            </div>
            <Field label="Category"><SimpleSelect value={f.category} onChange={set("category")} options={listOptions(cats)} placeholder="Uncategorized" testId="receipt-category-select" /></Field>
            <Field label="Entity"><SimpleSelect value={f.entity_id} onChange={set("entity_id")} allowNone scoped noneLabel="Unassigned" options={entityOptions(ents)} testId="receipt-entity-select" /></Field>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => save(false)} data-testid="receipt-save-btn">Save draft</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => save(true)} data-testid="receipt-post-btn">Approve & post</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UploadBar = ({ ents, onDone, onManual }) => {
  const cam = useRef();
  const file = useRef();
  const [entity, setEntity] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let last;
    for (const fl of files) {
      const fd = new FormData();
      fd.append("file", fl);
      if (entity) fd.append("entity_id", entity);
      try {
        last = (await api.post("/receipts/upload", fd)).data;
        toast.success(last.extracted ? `Extracted ${last.vendor || "receipt"} — review to post` : "Uploaded — enter details to post");
      } catch (e) {
        toast.error(errMsg(e));
      }
    }
    setBusy(false);
    onDone(last);
  };
  return (
    <div className="surface p-6 mb-6 grid-bg relative" data-testid="receipt-upload-zone"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1">
          <div className="font-semibold text-lg">Capture receipts</div>
          <p className="text-sm text-muted-foreground">Take a photo, drop images or PDFs. AI extracts vendor, date, amount, tax and currency — you approve before posting.</p>
        </div>
        <div className="w-full lg:w-56"><SimpleSelect value={entity} onChange={setEntity} allowNone scoped noneLabel="Assign later" options={entityOptions(ents)} testId="upload-entity-select" /></div>
        <div className="flex gap-2 flex-wrap">
          <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => upload(e.target.files)} data-testid="camera-input" />
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="receipt-file-input" />
          <Button onClick={() => cam.current.click()} disabled={busy} data-testid="camera-capture-btn"><Camera className="h-4 w-4 mr-1" />Camera</Button>
          <Button variant="outline" onClick={() => file.current.click()} disabled={busy} data-testid="upload-receipt-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{busy ? "Extracting…" : "PDF / Image"}
          </Button>
          <Button variant="ghost" onClick={() => onManual(entity)} data-testid="manual-receipt-btn"><Plus className="h-4 w-4 mr-1" />Manual</Button>
        </div>
      </div>
    </div>
  );
};

const ReceiptTable = ({ items, onOpen, onDelete, testId }) => (
  <div className="surface overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground border-b border-border">
        <tr>{["Vendor", "Date", "Amount", "Tax", "Category", "Entity", "Status", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-border">
        {items.map((r) => (
          <tr key={r.id} className="hover:bg-accent/50 cursor-pointer" onClick={() => onOpen(r)} data-testid={`receipt-row-${r.id}`}>
            <td className="px-4 py-3 font-medium">{r.vendor || <span className="text-muted-foreground">Unknown vendor</span>}
              {r.flags?.length > 0 && r.status !== "posted" && <span className="block text-xs font-normal text-red-500" data-testid={`receipt-flag-${r.id}`}>{r.flags[0].text}</span>}
              {r.auto_filed && <span className="block text-xs font-normal text-muted-foreground" data-testid={`receipt-auto-reason-${r.id}`}>{r.auto_corrected ? "Corrected by you" : r.auto_reason}</span>}</td>
            <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(r.date)}</td>
            <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{money(r.amount, r.currency)}</td>
            <td className="px-4 py-3 font-mono text-xs">{r.tax ?? "—"}</td>
            <td className="px-4 py-3">{r.category || <span className="text-amber-500 text-xs font-medium">Uncategorized</span>}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.entity_name || "—"}</td>
            <td className="px-4 py-3"><StatusPill status={r.status === "posted" ? (r.auto_filed ? "Auto-filed" : "Posted") : r.flags?.length ? "Flagged" : "Needs Review"} testId={`receipt-status-${r.id}`} /></td>
            <td className="px-4 py-3 text-right"><Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(r); }} data-testid={`receipt-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Receipts() {
  const { user } = useAuth();
  const state = useLoad(() => Promise.all([api.get("/receipts"), api.get("/entities"), api.get("/meta")]).then(([a, b, c]) => ({ ...a.data, ents: b.data, cats: c.data.receipt_categories })));
  const [open, setOpen] = useState(null);
  const d = state.data;
  const pending = (d?.items.filter((r) => r.status !== "posted") || []).sort((a, b) => (b.flags?.length || 0) - (a.flags?.length || 0));
  const posted = d?.items.filter((r) => r.status === "posted" && !r.auto_filed) || [];
  const autoFiled = d?.items.filter((r) => r.status === "posted" && r.auto_filed) || [];
  const del = async (r) => {
    await api.delete(`/receipts/${r.id}`);
    state.reload();
  };
  const manual = async (entityId) => {
    try {
      const r = (await api.post("/receipts", { currency: "CAD", entity_id: entityId || null })).data;
      state.reload();
      setOpen(r);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Module 6" title="Receipt management" subtitle="Every receipt reviewed before final posting.">
        {d && <div className="surface px-4 py-2 text-sm" data-testid="uncategorized-count">Uncategorized: <span className="font-mono font-semibold text-amber-500">{d.uncategorized}</span>
          {d.limit ? <span className="text-muted-foreground"> · {d.total}/{d.limit} used</span> : null}</div>}
      </PageHeader>
      <PageState state={state}>
        {d && (
          <>
            {user?.workspace?.role !== "viewer" && <UploadBar ents={d.ents} onDone={(last) => { state.reload(); if (last) setOpen(last); }} onManual={manual} />}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-8" data-testid="category-totals">
              {Object.entries(d.by_category).map(([c, v]) => (
                <div key={c} className="surface p-3"><div className="text-xs text-muted-foreground truncate">{c}</div><div className="font-mono text-sm font-semibold mt-1">{v.toFixed(2)}</div></div>
              ))}
            </div>
            <h2 className="font-semibold mb-3 flex items-center gap-2">Review queue <span className="font-mono text-xs rounded-full bg-amber-500/15 text-amber-500 px-2 py-0.5">{pending.length}</span></h2>
            {pending.length === 0 ? <Empty icon={ReceiptText} title="Queue is clear" text="All receipts have been reviewed and posted." testId="review-queue-empty" />
              : <ReceiptTable items={pending} onOpen={setOpen} onDelete={del} testId="review-queue-table" />}
            {autoFiled.length > 0 && (
              <>
                <h2 className="font-semibold mt-10 mb-1 flex items-center gap-2">Auto-filed by Claude <span className="font-mono text-xs rounded-full bg-emerald-500/15 text-emerald-500 px-2 py-0.5">{autoFiled.length}</span></h2>
                <p className="text-xs text-muted-foreground mb-3">Filed automatically because they matched your history. Spot-check and open any row to correct it — Claude learns from your changes.</p>
                <ReceiptTable items={autoFiled} onOpen={setOpen} onDelete={del} testId="auto-filed-table" />
              </>
            )}
            <h2 className="font-semibold mt-10 mb-3">Posted</h2>
            {posted.length === 0 ? <p className="text-sm text-muted-foreground">No posted receipts yet.</p> : <ReceiptTable items={posted} onOpen={setOpen} onDelete={del} testId="posted-receipts-table" />}
            <ReviewDialog receipt={open} onClose={() => setOpen(null)} ents={d.ents} cats={d.cats} reload={state.reload} />
          </>
        )}
      </PageState>
    </div>
  );
}
